import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import type { ReactElement } from 'react';
import styled from 'styled-components';

import { Rarity, ReferralRewardState, Sport } from '__generated__/globalTypes';
import discountCardBack from 'assets/rewards/discount-card-back.webp';
import { HeadlineL } from 'atoms/typography';
import { CardBack } from 'components/card/Back';
import { FRONTEND_ASSET_HOST } from 'constants/assets';
import { isType } from 'lib/gql';
import { getPositionInitials } from 'lib/positions';

import type { useReferralRewards_referralReward } from './__generated__/useReferralRewards.graphql';
import { useClaimReferralReward } from './useClaimReferralReward';

const DiscountCardBack = styled.img`
  width: var(--card-width);
  max-width: 100%;
`;

const isConversionCreditReward = (
  reward: useReferralRewards_referralReward
) => {
  return !!reward.conversionCredit;
};

const cardBackFactory = (
  reward: useReferralRewards_referralReward
): ReactElement | null => {
  if (isConversionCreditReward(reward)) {
    return <DiscountCardBack src={discountCardBack} alt="" width={20} />;
  }
  if (isType(reward.card, 'Card')) {
    const { card } = reward;
    const isCustomSeriesReward = card.rarityTyped === Rarity.custom_series;

    return (
      <CardBack
        path={
          card.backPictureUrl ||
          `${FRONTEND_ASSET_HOST}/cards/back/${card.rarityTyped}.svg`
        }
        sport={Sport.FOOTBALL}
        rarity={card.rarityTyped}
        radius={isCustomSeriesReward ? '30px' : '10px'}
      />
    );
  }
  if (isType(reward.card, 'BaseballCard') || isType(reward.card, 'NBACard')) {
    return (
      <CardBack
        sport={isType(reward.card, 'BaseballCard') ? Sport.BASEBALL : Sport.NBA}
        rarity={reward?.card.rarityTyped}
      />
    );
  }
  return null;
};

type Teasers = ReactElement[];

const teaserFactory = (reward: useReferralRewards_referralReward): Teasers => {
  if (isConversionCreditReward(reward)) {
    return [];
  }
  const { card } = reward;
  if (isType(card, 'Card')) {
    return [
      <img key={0} src={card.player.activeClub?.pictureUrl || ''} alt="" />,
      <HeadlineL as="h3" key={1}>
        {card.player.age}
      </HeadlineL>,
      <HeadlineL as="h3" key={2}>
        {card.player.cardPositions[0].slice(0, 3)}
      </HeadlineL>,
    ];
  }

  if (!card) return [];

  const initials = getPositionInitials(card.player.anyPositions[0]);

  if (isType(card, 'BaseballCard') || isType(card, 'NBACard')) {
    return [
      <img key={0} src={card.anyTeam?.pictureUrl || ''} alt="" />,
      <HeadlineL as="h3" key={1}>
        {initials}
      </HeadlineL>,
    ];
  }
  return [];
};

export const useReferralRewards = () => {
  const claim = useClaimReferralReward();

  const isClaimed = (reward_: useReferralRewards_referralReward | null) => {
    const reward = reward_;
    if (!reward) return false;
    return reward.claimableState === ReferralRewardState.CLAIMED;
  };

  const makeCardBackAndTeaser = (
    reward_: useReferralRewards_referralReward
  ) => {
    const reward = reward_;
    return {
      cardback: cardBackFactory(reward),
      teasers: teaserFactory(reward),
    };
  };

  return {
    claim,
    isClaimed,
    makeCardBackAndTeaser,
  };
};

useReferralRewards.fragments = {
  referralReward: gql`
    fragment useReferralRewards_referralReward on ReferralReward {
      id
      claimableAt
      claimableState
      conversionCredit {
        id
      }
      card: anyCard {
        slug
        sport
        rarityTyped
        player: anyPlayer {
          slug
          age
          cardPositions
          activeClub {
            slug
            pictureUrl
          }
          anyPositions
        }
        ... on Card {
          slug
          backPictureUrl
        }
        ... on BaseballCard {
          slug
          anyTeam {
            slug
            pictureUrl
          }
        }
        ... on NBACard {
          slug
          anyTeam {
            slug
            pictureUrl
          }
        }
      }
      ...useClaimReferralReward_referralReward
    }

    ${useClaimReferralReward.fragments.referralReward}
  ` as TypedDocumentNode<useReferralRewards_referralReward>,
};
